import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {diApi} from '@di-shared/api/di';
import {emitEventLog} from '@di-shared/features/analytics';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import {ROUTE_PATHS} from '@di-shared/routes';
import {sanitizeLicensePlateNumber} from '@di-shared/utils/vehicle';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useQuery} from '@tanstack/react-query';

export interface HighWayUnpaidFormType {
  licensePlateNumber: string;
}

export interface HighWayUnpaidFormErrorType {
  licensePlateNumberRegex: boolean;
}
export interface HighwayUnpaidSearchFormProps {
  vin: string;
}

function HighwayUnpaidSearchForm({vin}: HighwayUnpaidSearchFormProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const puid = useEnvironmentStore((state) => state.puid);
  const {openAlert} = useCcWebviewUtilsHelper();

  const [form, setForm] = useState<HighWayUnpaidFormType>({licensePlateNumber: ''});
  const [errors, setErrors] = useState<HighWayUnpaidFormErrorType>({
    licensePlateNumberRegex: false,
  });

  const {data: defaultRegistrationNumber, isPending: isDefaultRegistrationNumberLoading} = useQuery({
    queryKey: [getQueryKey(diApi.highwayUnpaid.getHighwayUnpaid), puid, vin],
    queryFn: () => diApi.highwayUnpaid.getHighwayUnpaid({puid, vin}),
    select: ({data}) => data.defaultRegistrationNumber,
  });

  useEffect(() => {
    if (defaultRegistrationNumber) {
      updateForm({licensePlateNumber: defaultRegistrationNumber});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRegistrationNumber]);

  function updateForm(partial: Partial<HighWayUnpaidFormType>) {
    const licensePlateNumber = sanitizeLicensePlateNumber(form.licensePlateNumber, partial.licensePlateNumber);

    const newForm: HighWayUnpaidFormType = {...form, ...partial, licensePlateNumber};
    setForm(newForm);
  }

  const [searchHighwayUnpaidFee] = usePendingHandler(
    async () => {
      setErrors((prev) => ({...prev, licensePlateNumberRegex: false}));
      const response = await diApi.highwayUnpaid.registerVehicle1({
        puid,
        requestBody: {
          licensePlateNumber: form.licensePlateNumber.trim(),
        },
      });

      if (response.data.unpaidAmount > 0) {
        await openAlert({
          title: t('T20402'),
          message: '',
        });
      }
      navigate(ROUTE_PATHS.HOME_FINANCE_TAB.getFullPath(vin));
    },
    async (error: any) => {
      if (error?.code === 4000) {
        setErrors((prev) => ({...prev, licensePlateNumberRegex: true}));
        return;
      }
      await openAlert({
        title: t('T40169'),
        message: `error [${error?.resultCode || error?.code || 9999}]`,
      });
    }
  );

  return (
    <>
      <main
        id="content"
        className="content"
      >
        <section className="section section_toll_inquiry">
          <div className="section_heading">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T20392')}
            </h2>
            <p
              className="section_heading_desc"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T20393')}
            </p>
          </div>
          <div className="service_info_area">
            <h3 className="info_title">{t('T20394')}</h3>
            <div className="input_info">
              <label
                htmlFor="vehicleRegistrationNumber01"
                className="blind"
              >
                {t('T20395')}
              </label>
              <div className={clsx('input_box input_box_field', {skeleton: isDefaultRegistrationNumberLoading})}>
                <input
                  type="text"
                  id="vehicleRegistrationNumber01"
                  className="input_text body_large_r"
                  inputMode="text"
                  placeholder={t('T20395')}
                  value={form.licensePlateNumber}
                  onChange={(e) => updateForm({licensePlateNumber: e.target.value})}
                  disabled={isDefaultRegistrationNumberLoading}
                />
                <div className="box_right">
                  <button
                    type="button"
                    className="btn_delete_input"
                  >
                    <span className="ic ic_filled_delete_20px">{t('T10154')}</span>
                  </button>
                </div>
              </div>
            </div>
            {errors.licensePlateNumberRegex && <p className="txt_error body_small_r">{t('T20398')}</p>}
          </div>

          <ul className="notice_list">
            <li className="notice_item">{t('T20399')}</li>
            <li className="notice_item">{t('T20400')}</li>
          </ul>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => {
            emitEventLog({
              event: 'public_service_interaction',
              data_action: 'search toll click',
              data_name: '통행료 조회하기',
            });
            searchHighwayUnpaidFee();
          }}
        >
          {t('T20401')}
        </button>
      </div>
    </>
  );
}

export default HighwayUnpaidSearchForm;
